import React, {useMemo} from 'react'
import {Box, CircularProgress, Typography} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import {STATUS} from 'src/constants/constants'
import {AdherencePeriod, buildAdherenceTimelineOptions, formatAdherencePercentage} from 'src/utils/adherenceCharts'

// The window is fixed by data-retrieval (last 12 months, whatever the "Data" filter says): the card only announces it.
const PERIOD_LABEL = 'Ultimi 12 mesi'

// Key `secondaryTherapyAdherence` of psp-backend GET /graphs/summaries/ (doctor): the adherence to
// the accessory therapies summed over all the doctor's patients, as in each patient's Analytics.
export interface SecondaryTherapyAdherence {
  completed: number
  total: number
  // null when nothing was due: "no data", never 0% adherence
  percentage: number | null
  timeline: AdherencePeriod[]
  // Every drug with doses due in the window, whatever drug filter is applied: the options of the drug filter
  drugNames?: string[]
}

interface SecondaryTherapyAdherenceCardProps {
  data?: SecondaryTherapyAdherence
  status?: string
}

export default function SecondaryTherapyAdherenceCard({data, status}: SecondaryTherapyAdherenceCardProps): JSX.Element {
  const color = useTheme().palette.primary.main
  const options = useMemo(
    () => (data ? buildAdherenceTimelineOptions({...data, byTherapy: []}, 'month', color) : undefined),
    [data, color],
  )

  let content: JSX.Element
  if (status === STATUS.PENDING) {
    content = (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
        <CircularProgress size={28} />
      </Box>
    )
  } else if (status === STATUS.REJECTED) {
    content = <EmptyState title="Dato non disponibile" />
  } else if (!data || !options || data.total === 0) {
    content = <EmptyState title="Nessuna assunzione prevista nel periodo" />
  } else {
    content = (
      <>
        <Box sx={{display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1.5, mb: 2}}>
          <Typography sx={{fontSize: 36, fontWeight: 400, lineHeight: 1.2, color: 'primary.main'}}>
            {formatAdherencePercentage(data.percentage)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`${data.completed} assunzioni su ${data.total} previste`}
          </Typography>
        </Box>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </>
    )
  }

  return (
    <Card
      title="Aderenza alle terapie"
      action={<Typography variant="body2" color="text.secondary">{PERIOD_LABEL}</Typography>}
      sx={{gridColumn: '1 / -1'}}
    >
      {content}
    </Card>
  )
}
